import type { JSX } from 'solid-js';
import { t } from '../i18n';
import {
  parseHygieneSummaryEvent,
  parseRunStageEvent,
  parseStatusEventName,
  type RunEventRow,
} from '../lib/fleetStream';

// THE TITLE OF ONE TIMELINE NODE, and the reason this file exists.
//
// `status` rows are 66,436 of prod's run events and exactly two sub-shapes were
// named, so 57,856 of them (87%) rendered the bare word "Status": `run_heartbeat`
// 10,979, `git_token_resolved` 7,226, `run_started` 7,140, plus every `babysit_*`,
// `review_*` and `thread_*` ping, all reading alike on a timeline that can run to
// 2,388 nodes.
//
// The fix DERIVES the title from the payload's own `event` token rather than
// enumerating the sub-shapes: the catalog wins where it has a better sentence,
// and anything else is humanised. A hand-kept list is exactly how the coverage
// got to 13%, and the box adds sub-events without asking this bundle.

/** Human label for a `TaskRunEventKind` (#298); an unknown kind falls back to the raw enum. */
export function kindLabel(kind: string): string {
  const key = `fleet.event_kind_${kind}`;
  return t.has(key) ? t(key) : kind;
}

/** `babysit_worktree_failed` → `Babysit worktree failed`. The wire token, made readable. */
export function humanise(event: string): string {
  const words = event.replace(/[_-]+/g, ' ').trim();
  return words === '' ? event : words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * What one row is called. `hygiene_summary` and `run_stage` keep the sentences
 * they already had; every other `status` row is named by its own sub-event.
 */
export function eventTitle(ev: RunEventRow): string {
  if (parseHygieneSummaryEvent(ev)) return t('fleet.event_hygiene_summary');
  if (ev.kind !== 'status') return kindLabel(ev.kind);
  const stage = parseRunStageEvent(ev);
  if (stage) {
    const stageKey = `fleet.stage_${stage.stage}`;
    if (t.has(stageKey)) return t(stageKey);
  }
  const event = parseStatusEventName(ev);
  if (event === null) return kindLabel(ev.kind);
  const key = `fleet.status_event_${event}`;
  return t.has(key) ? t(key) : humanise(event);
}

export type RunEventTitleProps = {
  readonly event: RunEventRow;
};

export function RunEventTitle(props: RunEventTitleProps): JSX.Element {
  return <p class="timeline__title">{eventTitle(props.event)}</p>;
}
